import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Gear, Lightning, FloppyDisk, User as UserIcon, CaretDown, MagnifyingGlass } from "@phosphor-icons/react";
import ConfirmDialog from "./ConfirmDialog";

/** Panel admin para editar:
 *  - Link de pago de Mercado Pago (pagalink)
 *  - Cuota mensual (monthly_fee) por atleta en una tabla
 */
export default function AdminPaymentsSettings({ onChanged }) {
  const [open, setOpen] = useState(false);
  const [mpUrl, setMpUrl] = useState("");
  const [mpApiEnabled, setMpApiEnabled] = useState(false);
  const [mpSaving, setMpSaving] = useState(false);
  const [users, setUsers] = useState([]);
  const [feeDraft, setFeeDraft] = useState({}); // user_id -> value
  const [linkDraft, setLinkDraft] = useState({}); // user_id -> url
  const [savingFor, setSavingFor] = useState(null);
  const [savingLinkFor, setSavingLinkFor] = useState(null);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [s, u, mp] = await Promise.all([
        api.get("/admin/settings"),
        api.get("/users"),
        api.get("/payments/mp-config"),
      ]);
      setMpUrl(s.data.mp_pagalink_url || "");
      setMpApiEnabled(Boolean(mp.data?.api_enabled));
      const athletes = u.data.filter((x) => x.role !== "admin");
      setUsers(athletes);
      const initial = {};
      const initialLinks = {};
      athletes.forEach((x) => {
        initial[x.user_id] = x.profile?.monthly_fee ?? "";
        initialLinks[x.user_id] = x.profile?.mp_pagalink_url ?? "";
      });
      setFeeDraft(initial);
      setLinkDraft(initialLinks);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const saveMpUrl = async () => {
    setMpSaving(true);
    try {
      const url = (mpUrl || "").trim();
      if (url && !/^https?:\/\//i.test(url)) {
        toast.error("El link debe comenzar con http:// o https://");
        setMpSaving(false);
        return;
      }
      await api.put("/admin/settings", { mp_pagalink_url: url });
      toast.success(url ? "Link de Mercado Pago actualizado" : "Link de MP eliminado");
      onChanged && onChanged();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setMpSaving(false);
    }
  };

  const saveFee = async (u) => {
    const raw = feeDraft[u.user_id];
    const val = raw === "" || raw == null ? null : Number(raw);
    if (val !== null && (!Number.isFinite(val) || val < 0)) {
      toast.error("Ingresá un monto válido");
      return;
    }
    setSavingFor(u.user_id);
    try {
      await api.put(`/admin/users/${u.user_id}/monthly-fee`, { monthly_fee: val });
      toast.success(`Cuota de ${u.name}: ${val ? `$${val.toLocaleString("es-AR")}` : "sin cuota"}`);
      setUsers((prev) => prev.map((x) => x.user_id === u.user_id
        ? { ...x, profile: { ...(x.profile || {}), monthly_fee: val ?? null } }
        : x));
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSavingFor(null);
    }
  };

  const saveLink = async (u) => {
    const raw = (linkDraft[u.user_id] ?? "").trim();
    if (raw && !/^https?:\/\//i.test(raw)) {
      toast.error("El link debe comenzar con http:// o https://");
      return;
    }
    setSavingLinkFor(u.user_id);
    try {
      await api.put(`/admin/users/${u.user_id}/mp-link`, { mp_pagalink_url: raw });
      toast.success(raw ? `Link personal seteado para ${u.name}` : `${u.name} ahora usa el link global`);
      setUsers((prev) => prev.map((x) => x.user_id === u.user_id
        ? { ...x, profile: { ...(x.profile || {}), mp_pagalink_url: raw || null } }
        : x));
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSavingLinkFor(null);
    }
  };

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    if (!s) return users;
    return users.filter((u) =>
      (u.name || "").toLowerCase().includes(s) ||
      (u.email || "").toLowerCase().includes(s),
    );
  }, [users, search]);

  const hasChanges = (u) => String(feeDraft[u.user_id] ?? "") !== String(u.profile?.monthly_fee ?? "");
  const hasLinkChanges = (u) => String(linkDraft[u.user_id] ?? "") !== String(u.profile?.mp_pagalink_url ?? "");

  return (
    <div className="card-base p-0 mb-5" data-testid="admin-payments-settings">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-2 p-5 text-left hover:bg-[#0A0A0A] transition-colors"
        data-testid="settings-toggle"
      >
        <Gear size={20} weight="duotone" className="text-primary-app" />
        <div className="flex-1">
          <div className="font-display font-black uppercase text-xl tracking-tighter">Configuración de pagos</div>
          <div className="text-[11px] text-muted mt-0.5">
            Link global · Cuotas · Link MP individual por atleta
          </div>
        </div>
        <CaretDown size={16} weight="bold" className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="border-t border-app p-5 sm:p-6 space-y-7">
          {/* MP Pagalink */}
          <div data-testid="mp-url-section">
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <Lightning size={16} weight="fill" className="text-[#009EE3]" />
              <label className="text-xs uppercase tracking-widest font-bold">Link de Mercado Pago</label>
              {mpApiEnabled && (
                <span className="text-[8px] uppercase tracking-widest font-bold bg-[#32D74B] text-black px-1.5 py-0.5">
                  API conectada · auto
                </span>
              )}
            </div>
            <p className="text-[11px] text-muted mb-3 leading-relaxed">
              {mpApiEnabled
                ? "La API de Mercado Pago está configurada: cada cobro genera una preference dinámica con el monto correcto y se aprueba automáticamente vía webhook. El link de abajo solo se usa como respaldo."
                : "Pegá acá tu link de cobro de MP (mpago.la/... o mercadopago.com.ar/payments/...). El botón del atleta abrirá este link."}
            </p>
            <div className="flex flex-wrap gap-2">
              <input
                className="input-base flex-1 min-w-[260px]"
                value={mpUrl}
                onChange={(e) => setMpUrl(e.target.value)}
                placeholder="https://mpago.la/XXXXXXX"
                data-testid="mp-url-input"
              />
              <button
                onClick={saveMpUrl}
                disabled={mpSaving}
                className="btn-primary flex items-center gap-2 disabled:opacity-40"
                data-testid="save-mp-url"
              >
                <FloppyDisk size={16} weight="bold" />
                {mpSaving ? "Guardando..." : "Guardar"}
              </button>
            </div>
          </div>

          {/* Bulk monthly fee */}
          <div data-testid="bulk-fee-section" className="bg-[#0A0A0A] border border-app p-4">
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <UserIcon size={16} weight="duotone" className="text-[#FFD60A]" />
              <label className="text-xs uppercase tracking-widest font-bold">Aplicar cuota masiva</label>
            </div>
            <p className="text-[11px] text-muted mb-3 leading-relaxed">
              Pone la misma cuota mensual a TODOS los atletas en un click.
              Útil para aumentos de precios (ej: enero, julio).
              Los atletas reciben una notificación con el nuevo monto.
            </p>
            <BulkFeeRow onApplied={load} users={users} />
          </div>

          {/* Monthly fee por atleta */}
          <div data-testid="monthly-fees-section">
            <div className="flex items-center gap-2 mb-3 flex-wrap">
              <UserIcon size={16} weight="duotone" className="text-primary-app" />
              <label className="text-xs uppercase tracking-widest font-bold">Cuota mensual y link MP por atleta</label>
              <div className="ml-auto relative">
                <MagnifyingGlass size={12} weight="bold" className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted" />
                <input
                  className="input-base !py-1.5 !pl-7 text-xs w-56"
                  placeholder="Buscar atleta..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  data-testid="fee-search"
                />
              </div>
            </div>

            {loading ? (
              <div className="text-xs text-muted py-4 text-center">Cargando...</div>
            ) : filtered.length === 0 ? (
              <div className="text-xs text-muted py-4 text-center">Sin atletas {search ? "que coincidan" : ""}.</div>
            ) : (
              <div className="border border-app max-h-96 overflow-y-auto" data-testid="fees-table">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-[#0A0A0A] z-10">
                    <tr className="text-left text-[10px] uppercase tracking-widest text-muted border-b border-app">
                      <th className="py-2 px-3">Atleta</th>
                      <th className="py-2 px-3 w-40">Cuota (ARS)</th>
                      <th className="py-2 px-3 w-64">Link MP personal</th>
                      <th className="py-2 px-3 w-24 text-right">Acción</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((u) => {
                      const changed = hasChanges(u);
                      const linkChanged = hasLinkChanges(u);
                      const hasPersonal = Boolean(u.profile?.mp_pagalink_url);
                      return (
                      <tr key={u.user_id} className="border-b border-app hover:bg-[#0A0A0A]" data-testid={`fee-row-${u.user_id}`}>
                        <td className="py-2 px-3">
                          <div className="font-bold flex items-center gap-1.5">
                            {u.name}
                            {hasPersonal && (
                              <span className="text-[8px] uppercase tracking-widest font-bold bg-[#009EE3] text-white px-1 py-0.5" title="Usa link MP personal">
                                link
                              </span>
                            )}
                          </div>
                          <div className="text-[11px] text-muted">{u.email}</div>
                        </td>
                        <td className="py-2 px-3">
                          <div className="flex gap-1 items-center">
                            <input
                              type="number" min="0" step="100"
                              className="input-base !py-1.5 text-sm flex-1"
                              value={feeDraft[u.user_id] ?? ""}
                              onChange={(e) => setFeeDraft({ ...feeDraft, [u.user_id]: e.target.value })}
                              placeholder="—"
                              data-testid={`fee-input-${u.user_id}`}
                            />
                            <button
                              onClick={() => saveFee(u)}
                              disabled={!changed || savingFor === u.user_id}
                              className={`btn-secondary !py-1.5 !px-2 text-xs flex-shrink-0 ${changed ? "!border-[#32D74B] !text-[#32D74B]" : "opacity-40 cursor-not-allowed"}`}
                              data-testid={`save-fee-${u.user_id}`}
                              title="Guardar cuota"
                            >
                              <FloppyDisk size={12} weight="bold" />
                            </button>
                          </div>
                        </td>
                        <td className="py-2 px-3">
                          <div className="flex gap-1 items-center">
                            <input
                              type="url"
                              className="input-base !py-1.5 text-sm flex-1 font-mono text-xs"
                              value={linkDraft[u.user_id] ?? ""}
                              onChange={(e) => setLinkDraft({ ...linkDraft, [u.user_id]: e.target.value })}
                              placeholder="Global"
                              data-testid={`link-input-${u.user_id}`}
                            />
                            <button
                              onClick={() => saveLink(u)}
                              disabled={!linkChanged || savingLinkFor === u.user_id}
                              className={`btn-secondary !py-1.5 !px-2 text-xs flex-shrink-0 ${linkChanged ? "!border-[#009EE3] !text-[#009EE3]" : "opacity-40 cursor-not-allowed"}`}
                              data-testid={`save-link-${u.user_id}`}
                              title="Guardar link MP"
                            >
                              <FloppyDisk size={12} weight="bold" />
                            </button>
                          </div>
                        </td>
                        <td className="py-2 px-3 text-right text-[10px] text-muted">
                          {hasPersonal ? "Personal" : "Global"}
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}



/** Componente interno: aplicar cuota mensual masiva a todos los atletas */
function BulkFeeRow({ onApplied, users }) {
  const [amount, setAmount] = React.useState("");
  const [excluded, setExcluded] = React.useState([]);
  const [submitting, setSubmitting] = React.useState(false);
  const [confirm, setConfirm] = React.useState(false);
  const [sendingAnnual, setSendingAnnual] = React.useState(false);

  const target = users.length - excluded.length;

  const submit = async () => {
    const n = Number(amount);
    if (!n || n <= 0) {
      toast.error("Ingresá un monto válido");
      return;
    }
    setConfirm(true);
  };

  const performBulk = async () => {
    setSubmitting(true);
    setConfirm(false);
    try {
      const { data } = await api.post("/admin/users/bulk-monthly-fee", {
        amount: Number(amount),
        exclude_user_ids: excluded,
        notify: true,
      });
      toast.success(`Cuota aplicada a ${data.updated} atleta${data.updated === 1 ? "" : "s"}`);
      setAmount("");
      setExcluded([]);
      onApplied?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const sendAnnualSummaries = async () => {
    setSendingAnnual(true);
    try {
      const { data } = await api.post("/admin/payments/send-annual-summaries");
      toast.success(`Resumen ${data.year}: ${data.sent} enviados, ${data.skipped} omitidos`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSendingAnnual(false);
    }
  };

  return (
    <>
      <div className="flex flex-wrap gap-2 items-center">
        <input
          type="number" min="1" step="1"
          className="input-base !py-2 w-40 text-sm"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          placeholder="Nuevo monto"
          data-testid="bulk-fee-amount"
        />
        <span className="text-xs text-muted">
          se aplicará a <span className="text-white font-bold">{target}</span> atleta{target === 1 ? "" : "s"}
        </span>
        <button
          onClick={submit}
          disabled={submitting || !amount}
          className="btn-primary !py-2 !px-4 text-sm flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
          data-testid="bulk-fee-submit"
        >
          {submitting ? "Aplicando..." : "Aplicar a todos"}
        </button>
        <button
          onClick={sendAnnualSummaries}
          disabled={sendingAnnual}
          className="btn-secondary !py-2 !px-4 text-sm flex items-center gap-2 ml-auto"
          title="Envía por email el resumen anual del año cerrado a cada atleta"
          data-testid="send-annual-summaries"
        >
          {sendingAnnual ? "Enviando..." : "📧 Enviar resumen anual"}
        </button>
      </div>

      {users.length > 0 && (
        <details className="mt-3 text-xs">
          <summary className="cursor-pointer text-muted hover:text-white">
            Excluir atletas... ({excluded.length} excluidos)
          </summary>
          <div className="mt-2 max-h-40 overflow-y-auto bg-[#141414] border border-app p-2 grid grid-cols-1 sm:grid-cols-2 gap-1">
            {users.map((u) => (
              <label key={u.user_id} className="flex items-center gap-2 cursor-pointer hover:bg-[#1a1a1a] px-2 py-1">
                <input
                  type="checkbox"
                  checked={excluded.includes(u.user_id)}
                  onChange={(e) => setExcluded((arr) => e.target.checked ? [...arr, u.user_id] : arr.filter((x) => x !== u.user_id))}
                  data-testid={`bulk-exclude-${u.user_id}`}
                />
                <span className="truncate">{u.name}</span>
                {u.profile?.monthly_fee && (
                  <span className="text-muted text-[10px] ml-auto">${u.profile.monthly_fee.toLocaleString("es-AR")}</span>
                )}
              </label>
            ))}
          </div>
        </details>
      )}

      <ConfirmDialog
        open={confirm}
        title="Aplicar cuota masiva"
        body={`¿Confirmás aplicar $${Number(amount || 0).toLocaleString("es-AR")} a ${target} atleta${target === 1 ? "" : "s"}?\nCada atleta recibirá una notificación con el nuevo monto.`}
        confirmLabel="Aplicar a todos"
        onConfirm={performBulk}
        onCancel={() => setConfirm(false)}
      />
    </>
  );
}
